import { FC, ReactNode, createContext, useContext, useEffect, useState } from 'react';

import useBranding from 'store/branding';
import useQueryParams from 'hooks/useQueryParams';
import { verifyEmail } from 'api/requests/account';

import { getPartnerRoutePaths } from '../config/routingConfig';
import { PartnerNotificationContainer, usePartnerNotification } from '../components/PartnerNotification';

import { RobibetDataProvider } from './RobibetDataContext';

interface PartnerRoutingContextType {
  routes: {
    home: string;
    about: string;
    instructions: string;
    signIn: string;
    signUp: string;
    forgotPassword: string;
    affiliateAgreement: string;
  };
  partnerId: number;
  isReady: boolean;
}

const defaultRoutes = {
  home: '/',
  about: '/about',
  affiliateAgreement: '/affiliate-agreement',
  forgotPassword: '/forgot-password',
  instructions: '/instructions',
  signIn: '/sign-in',
  signUp: '/sign-up',
};

const PartnerRoutingContext = createContext<PartnerRoutingContextType>({
  routes: defaultRoutes,
  partnerId: 0,
  isReady: false,
});

export const usePartnerRouting = (): PartnerRoutingContextType => useContext(PartnerRoutingContext);

interface PartnerRoutingProviderProps {
  children: ReactNode;
}

const EmailVerificationHandler: FC = () => {
  const queryParams = useQueryParams();
  const { showNotification } = usePartnerNotification();
  const emailVerifyQuery = queryParams.get('verifyEmail');

  useEffect(() => {
    if (emailVerifyQuery) {
      (async (): Promise<void> => {
        try {
          await verifyEmail({ verifyEmailToken: emailVerifyQuery as string });
          showNotification({
            type: 'success',
            message: 'Your email has been successfully verified!',
          });
        } catch (error) {
          console.error(error);
          showNotification({
            type: 'error',
            message: 'Something went wrong during verification. Please try again.',
          });
        }
      })();
    }
  }, [emailVerifyQuery, showNotification]);

  return null;
};

export const PartnerRoutingProvider: FC<PartnerRoutingProviderProps> = ({ children }) => {
  const [brandingState] = useBranding();
  const [isReady, setIsReady] = useState(false);
  const partnerId = brandingState?.settings?.id || 0;
  const routes = getPartnerRoutePaths(partnerId) || defaultRoutes;

  // Remove external Cloudflare preloader only when Robibet (partnerId 9) is active
  useEffect(() => {
    if (partnerId !== 9) return;
    const el = typeof document !== 'undefined' ? document.getElementById('preloader') : null;
    if (el) el.remove();
  }, [partnerId]);

  useEffect(() => {
    // Load Tailwind CDN globally for all partner pages
    if (!document.getElementById('partners-tailwind-cdn')) {
      const script = document.createElement('script');
      script.id = 'partners-tailwind-cdn';
      script.src = 'https://cdn.tailwindcss.com';
      script.onload = (): void => {
        // Wait a bit more for Tailwind to process the DOM
        setTimeout(() => setIsReady(true), 100);
      };
      document.head.appendChild(script);
    } else {
      setIsReady(true);
    }

    // Load Poppins font
    if (!document.getElementById('partners-font-poppins')) {
      const fontLink = document.createElement('link');
      fontLink.id = 'partners-font-poppins';
      fontLink.rel = 'stylesheet';
      fontLink.href = 'https://fonts.cdnfonts.com/css/poppins';
      document.head.appendChild(fontLink);
    }

    // Load custom styles
    if (!document.getElementById('partners-custom-styles')) {
      const style = document.createElement('style');
      style.id = 'partners-custom-styles';
      style.innerHTML = `
:root {
  --partners-background: #09101e;
  --partners-foreground: #ffffff;
  --partners-accent: rgba(197, 237, 0, 1);
  --partners-accent-hover: rgba(133, 254, 0, 1);
}

.bodyBg {
  background-color: var(--partners-background) !important;
  font-family: 'Poppins', sans-serif !important;
  background-repeat: no-repeat !important;
  min-height: 100vh !important;
  width: 100% !important;
}

.font-poppins {
  font-family: 'Poppins', sans-serif !important;
}

.bg-accent {
  background-color: var(--partners-background) !important;
}

.text-foreground {
  color: var(--partners-foreground);
}

.text-background {
  color: var(--partners-background);
}

.bg-background {
  background-color: var(--partners-background) !important;
}

.bg-accent_two {
  background-color: var(--partners-accent) !important;
}

.text-accent {
  color: var(--partners-background);
}

.text-accent_two {
  color: var(--partners-accent);
}

.border-accent {
  border-color: var(--partners-accent) !important;
}

.hover\\:bg-accent_two:hover {
  background-color: var(--partners-accent) !important;
}

.hover\\:text-accent_two:hover {
  color: var(--partners-accent) !important;
}

.partners-button {
  transition: all 0.5s ease;
}

.partners-button:hover {
  background-color: rgba(197, 237, 0, 0.5) !important;
  color: var(--partners-background) !important;
}

.swiper-pagination-bullet {
  background-color: white !important;
  width: 12px !important;
  height: 12px !important;
  opacity: 1 !important;
  margin: 0 10px !important;
}

.swiper-pagination-bullet-active {
  background-color: var(--partners-accent) !important;
  transform: scale(1.2);
}

.swiper-pagination {
  bottom: 0px !important;
  text-align: center;
}

.swiper {
  overflow-y: auto !important;
  height: 30rem !important;
}

h1 {
  font-size: 37px;
  font-weight: 600;
  text-align: center;
  line-height: 2.5rem;
  text-transform: uppercase;
}

@media (min-width: 1024px) {
  h1 {
    font-size: 38px;
    line-height: 5rem;
  }
}
      `;
      document.head.appendChild(style);
    }
  }, []);

  const value = {
    routes,
    partnerId,
    isReady,
  };

  if (!isReady) {
    return null;
  }

  return (
    <PartnerRoutingContext.Provider value={value}>
      <RobibetDataProvider>
        <PartnerNotificationContainer />
        <EmailVerificationHandler />
        {children}
      </RobibetDataProvider>
    </PartnerRoutingContext.Provider>
  );
};
